import React, { useEffect } from 'react';
import {
  Box, TextField, Select, MenuItem, IconButton, FormControl, InputLabel, Grid, Button, Typography
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

/**
 * AtelierForm
 * - tolerant handling of groupe and ateliers values (string or object)
 * - InputLabel shrink + renderValue to avoid label overlap
 * - onChange normalizes groupe to string (name)
 * - rest of UI (emoji, compacts fields) preserved
 */
export default function AtelierForm({
  index,
  data,
  onChange,
  onRemove,
  groupes = [],
  ateliersEx = [],
  mode = 'GUIDE',
  mappings = [],
  color = ''
}) {
  const labels = ['Trop facile', 'Facile', 'Moyen', 'Difficile', 'Impossible'];
  const emojis = ['😎', '🤗', '☺️', '😅', '🥵'];

  // normalize muscle name from data.groupe (could be string or object)
  const muscleName = typeof data.groupe === 'string' ? data.groupe : (data.groupe?.name || data.groupe?.label || '');

  // build allowed ateliers for selected muscle in a tolerant way (mapping.muscle may be string or object)
  const allowedAteliersForMuscle = (() => {
    if (mode === 'GUIDE') {
      const mp = mappings.find(m => {
        if (!m) return false;
        const mm = m.muscle;
        const mmName = typeof mm === 'string' ? mm : (mm?.name || mm?.label || '');
        return mmName === muscleName;
      });
      return Array.isArray(mp?.ateliers) ? mp.ateliers : [];
    }
    return ateliersEx || [];
  })();

  // If current atelier becomes invalid in GUIDE mode -> clear it
  useEffect(() => {
    if (mode === 'GUIDE' && data.atelierNom) {
      if (!allowedAteliersForMuscle.includes(data.atelierNom)) {
        onChange({ atelierNom: '' });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [muscleName, mode, mappings]);

  const ressenti = data.ressenti ?? 1;

  const setRessenti = (val) => {
    onChange({ ressenti: val });
  };

  // renderValue for Select to display regardless of underlying type
  const renderSelectValue = (selected) => {
    if (!selected) return 'Sélectionner';
    if (typeof selected === 'string') return selected;
    return selected.name || selected.label || String(selected);
  };

  // helper to normalize groupe options (groupes array might contain strings or objects)
  const groupeOptions = (groupes || []).map(g => (typeof g === 'string' ? g : (g?.name || g?.label || '')));

  return (
    <Box sx={{
      border: '1px solid #ddd',
      p: 2,
      borderRadius: 1,
      mb: 1,
      display: 'flex',
      alignItems: 'center',
      backgroundColor: '#fff',
      // colored left stripe to indicate atelier color
      borderLeft: color ? `8px solid ${color}` : '8px solid transparent'
    }}>
      <Grid container spacing={2} alignItems="center">
        {/* Groupe (larger and taller so full labels show) */}
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth sx={{
            minWidth: 240,
            '& .MuiInputBase-root': { minHeight: 48, alignItems: 'center' }
          }}>
            <InputLabel id={`groupe-${index}`} shrink={!!muscleName}>Groupe musculaire</InputLabel>
            <Select
              labelId={`groupe-${index}`}
              value={muscleName ?? ''}
              label="Groupe musculaire"
              onChange={e => {
                const val = e.target.value;
                // value from options is already a string (we normalized groupeOptions), but keep tolerant
                const normalized = typeof val === 'string' ? val : (val?.name || val?.label || '');
                onChange({ groupe: normalized });
              }}
              renderValue={renderSelectValue}
              size="small"
            >
              <MenuItem value="">Sélectionner</MenuItem>
              {groupeOptions.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>

        {/* Atelier (taller like before) */}
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth sx={{
            minWidth: 180,
            '& .MuiInputBase-root': { minHeight: 48, alignItems: 'center' }
          }}>
            <InputLabel id={`atelier-${index}`} shrink={!!(data.atelierNom)}>Atelier</InputLabel>
            <Select
              labelId={`atelier-${index}`}
              value={data.atelierNom ?? ''}
              label="Atelier"
              onChange={e => onChange({ atelierNom: e.target.value })}
              disabled={mode === 'GUIDE' && !muscleName}
              renderValue={renderSelectValue}
              sx={{ width: '100%' }}
              size="small"
            >
              <MenuItem value="">Sélectionner</MenuItem>
              {(mode === 'GUIDE' ? allowedAteliersForMuscle : ateliersEx).map(a => (
                <MenuItem key={a} value={a}>{a}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Séries (réduit en largeur, hauteur small) */}
        <Grid item xs={6} sm={1}>
          <TextField
            label="Séries"
            type="number"
            value={data.series ?? ''}
            onChange={e => onChange({ series: e.target.value === '' ? '' : Number(e.target.value) })}
            fullWidth
            size="small"
            inputProps={{ style: { padding: '8px 8px' } }}
          />
        </Grid>

        {/* Répétitions (réduit en largeur) */}
        <Grid item xs={6} sm={1}>
          <TextField
            label="Réps"
            type="number"
            value={data.repetitions ?? ''}
            onChange={e => onChange({ repetitions: e.target.value === '' ? '' : Number(e.target.value) })}
            fullWidth
            size="small"
            inputProps={{ style: { padding: '8px 8px' } }}
          />
        </Grid>

        {/* Poids / Distance (réduit en largeur) */}
        <Grid item xs={12} sm={1.5}>
          <TextField
            label="Poids/Dist"
            value={data.poids ?? ''}
            onChange={e => onChange({ poids: e.target.value })}
            fullWidth
            size="small"
            inputProps={{ style: { padding: '8px 8px' } }}
          />
        </Grid>

        {/* Ressenti : emojis + petits libellés (sélection visible) */}
        <Grid item xs={12} sm={1.5}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', justifyContent: 'center' }}>
              {emojis.map((emo, i) => {
                const val = i + 1;
                const selected = val === ressenti;
                return (
                  <Button
                    key={emo}
                    onClick={() => setRessenti(val)}
                    variant={selected ? 'contained' : 'text'}
                    sx={{
                      minWidth: 36,
                      height: 36,
                      padding: 0,
                      borderRadius: '50%',
                      fontSize: 18,
                      lineHeight: 1,
                      backgroundColor: selected ? '#E6D5FF' : 'transparent',
                      color: selected ? '#4B2166' : 'inherit',
                      boxShadow: selected ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                      '&:hover': { backgroundColor: selected ? '#D4C0FF' : '#f3f3f3' }
                    }}
                    aria-label={labels[i]}
                    size="small"
                  >
                    <span style={{ transform: 'translateY(-1px)' }}>{emo}</span>
                  </Button>
                );
              })}
            </Box>

            <Box sx={{ display: 'flex', gap: 0.75, mt: 0.5, justifyContent: 'center', flexWrap: 'nowrap' }}>
              {labels.map((lab, i) => {
                const isSelected = (i + 1) === ressenti;
                return (
                  <Typography
                    key={lab}
                    sx={{
                      fontSize: 11,
                      color: isSelected ? '#4B2166' : 'text.secondary',
                      fontWeight: isSelected ? 700 : 400,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {lab}
                  </Typography>
                );
              })}
            </Box>
          </Box>
        </Grid>

        {/* Supprimer */}
        <Grid item xs={12} sm={0.5} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <IconButton color="error" size="small" onClick={onRemove} aria-label="Supprimer atelier">
            <DeleteIcon />
          </IconButton>
        </Grid>
      </Grid>
    </Box>
  );
}